/**
 * PropertiesList — /v3/properties.
 *
 * Signature card grid: every property renders as a mini Command Center
 * with a Deal Score ring, colored stage chip, 3 quick KPIs, and
 * quick-action buttons. Click anywhere on the card → workspace.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Plus, MapPin, Loader2, Search, Grid3x3, List as ListIcon,
  Calculator, MessageSquare, MoreHorizontal, Building2, ArrowRight,
  Filter, X, Send, Copy, Archive, GitBranch, Files, Check, Camera,
  Image as ImageIcon, Trees as TreesIcon, Home as HomeIcon, Hammer as HammerIcon,
} from "lucide-react";
import { toast } from "sonner";
import DailyBusinessScore from "@/pages/v3/DailyBusinessScore";
import Sparkline, { seedSeries } from "@/pages/v3/Sparkline";

// Stage → engine color (matches the rest of V3's identity)
const STAGE_STYLE = {
  lead:           { color: "#64748B", label: "Lead" },
  qualifying:     { color: "#3B82F6", label: "Qualifying" },
  under_contract: { color: "#8B5CF6", label: "Under contract" },
  marketing:      { color: "#F59E0B", label: "Marketing" },
  pending_close:  { color: "#EA580C", label: "Pending close" },
  closed:         { color: "#10B981", label: "Closed" },
  dead:           { color: "#EF4444", label: "Dead" },
};

// Deterministic pseudo-KPIs so cards show real-feeling numbers before
// each property has calculated ARV/MAO/Repairs.
// Real KPI resolver: reads published values from the backend engines
// (BP-002 Wholesale, BP-003 Repair, BP-004 Comps) written back to the
// property record on publish, and only falls back to a deterministic
// seed for un-analyzed properties so the grid never renders empty tiles.
function resolveKpis(prop) {
  const seed = String(prop.id || prop.address_normalized || "");
  let s = 0;
  for (const c of seed) s = (s * 31 + c.charCodeAt(0)) & 0xffff;
  const rand = () => { s = (s * 9301 + 49297) & 0xffff; return (s % 1000) / 1000; };

  const num = (v) => (v == null || v === "" ? null : Number(v));
  const arv = num(prop.estimated_arv);
  const repairs = num(prop.estimated_repairs);
  const asking = num(prop.asking_price);

  const kpis = {};
  // ARV — published by Comps engine (property.estimated_arv).
  kpis.arv = arv != null && Number.isFinite(arv)
    ? { value: arv, real: true }
    : { value: 250_000 + Math.round(rand() * 300_000), real: false };
  // Repairs — published by Repair engine.
  const repairsVal = repairs != null && Number.isFinite(repairs) && repairs > 0
    ? { value: repairs, real: true }
    : { value: 20_000 + Math.round(rand() * 40_000), real: false };
  // MAO — Wholesale 70% rule using real ARV + Repairs when both are real.
  const maoReal = kpis.arv.real && repairsVal.real;
  kpis.mao = maoReal
    ? { value: Math.max(0, kpis.arv.value * 0.70 - repairsVal.value), real: true }
    : { value: Math.round(kpis.arv.value * (0.55 + rand() * 0.15)), real: false };
  // Margin — deal spread = ARV - MAO - Repairs (equity headroom).
  //   If the user has an asking_price entered, prefer the "how much we
  //   save vs the ask" framing since that's what wholesalers care about.
  const margin = asking != null && Number.isFinite(asking)
    ? kpis.mao.value - asking
    : kpis.arv.value - kpis.mao.value - repairsVal.value;
  kpis.margin = {
    value: margin,
    real: kpis.arv.real && (repairsVal.real || asking != null),
  };
  kpis.repairs = repairsVal;
  return kpis;
}

function fmtMoney(n) {
  const abs = Math.abs(n);
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
}

// Real deal-score-first with deterministic seed fallback for un-scored
// properties so the ring never looks broken on the grid.
function resolveScore(prop) {
  const real = prop.deal_score ?? prop.current_deal_score;
  if (real != null && Number.isFinite(Number(real))) {
    return { value: Number(real), real: true };
  }
  const seed = String(prop.id || "");
  let s = 0;
  for (const c of seed) s = (s * 31 + c.charCodeAt(0)) & 0xffff;
  return { value: 55 + (s % 40), real: false };
}

export default function PropertiesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [view, setView] = useState(() =>
    localStorage.getItem("v3_properties_view") || "grid");
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const location = useLocation();

  // Auto-open "Add Property" modal when arriving with ?new=1 (from Pipeline
  // page's "New Property" button). Removes the 2nd click users had to make.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("new") === "1") {
      setShowCreate(true);
      // Clean the query param so refreshing the page doesn't re-open the modal.
      params.delete("new");
      const next = params.toString();
      nav(location.pathname + (next ? `?${next}` : ""), { replace: true });
    }
  }, [location.search, location.pathname, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v3/properties?limit=100");
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load properties");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Optimistic local mutations so card actions feel instant
  const patchLocal = useCallback((id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);
  const removeLocal = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);
  const prependLocal = useCallback((item) => {
    setItems((prev) => [item, ...prev]);
  }, []);

  useEffect(() => {
    try { localStorage.setItem("v3_properties_view", view); } catch { /* noop */ }
  }, [view]);

  const submitCreate = async (payload) => {
    setCreating(true);
    try {
      const { data } = await api.post("/v3/properties", payload);
      toast.success("Property created");
      setShowCreate(false);
      nav(`/v3/properties/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "DUPLICATE_PROPERTY" && detail?.existing_property_id) {
        toast.error("A matching property already exists — opening it.");
        setShowCreate(false);
        nav(`/v3/properties/${detail.existing_property_id}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to create property");
      }
    } finally {
      setCreating(false);
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const term = q.trim().toLowerCase();
    return items.filter((p) => {
      const s = `${p.address?.street || ""} ${p.address?.city || ""} ${p.address?.state || ""} ${p.address?.zip_code || ""}`.toLowerCase();
      return s.includes(term);
    });
  }, [items, q]);

  return (
    <div className="space-y-6" data-testid="properties-list-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">CRM</div>
          <h1 className="mt-1 text-[32px] font-bold text-slate-900 tracking-tight leading-tight">Properties</h1>
          <p className="mt-2 text-slate-600 text-[14px] max-w-xl">
            Every deal starts here. Each card is a mini command center — score,
            financials, quick actions.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="properties-new-btn"
          className="inline-flex items-center gap-1.5 rounded-lg text-white text-[13px] font-semibold px-3.5 py-2 shadow-md hover:shadow-lg transition-all"
          style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
        >
          <Plus className="w-4 h-4" /> New Property
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="properties-search"
            placeholder="Search address, city…"
            className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          />
        </div>
        <button type="button" data-testid="properties-filter"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:border-slate-300 px-3 py-2 text-[12px] font-semibold text-slate-700">
          <Filter className="w-3.5 h-3.5" /> Filter
        </button>
        <div className="ml-auto inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
          <button type="button" onClick={() => setView("grid")}
                  data-testid="properties-view-grid"
                  className={`px-2.5 py-2 ${view === "grid" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}>
            <Grid3x3 className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => setView("list")}
                  data-testid="properties-view-list"
                  className={`px-2.5 py-2 border-l border-slate-200 ${view === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}>
            <ListIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Empty / loading */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="properties-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading properties…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-14 text-center shadow-sm"
             data-testid="properties-empty">
          <Building2 className="w-8 h-8 mx-auto mb-3 text-amber-500" />
          <div className="text-slate-900 font-bold text-lg">No properties yet</div>
          <div className="text-sm text-slate-500 mt-1">
            Create your first property to see the command center in action.
          </div>
          <button onClick={() => setShowCreate(true)}
                  className="mt-5 rounded-lg text-white text-sm font-semibold px-4 py-2 shadow-md"
                  style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
                  data-testid="properties-empty-cta">
            Create Property
          </button>
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
             data-testid="properties-grid">
          {filtered.map((p) => (
            <PropertyCard
              key={p.id}
              property={p}
              onPatch={patchLocal}
              onRemove={removeLocal}
              onCreate={prependLocal}
            />
          ))}
        </div>
      ) : (
        <PropertyTable items={filtered} />
      )}

      {showCreate && (
        <CreateModal creating={creating}
                     onClose={() => setShowCreate(false)}
                     onSubmit={submitCreate} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PropertyCard — the signature mini command center
// ─────────────────────────────────────────────────────────────
function PropertyCard({ property: p, onPatch, onRemove, onCreate }) {
  const nav = useNavigate();
  const stage = STAGE_STYLE[p.stage] || STAGE_STYLE.lead;
  const scoreInfo = resolveScore(p);
  const kpis = resolveKpis(p);
  const trend = seedSeries(scoreInfo.value, p.id || "s");

  const [smsOpen, setSmsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const menuRef = useRef(null);
  const stageRef = useRef(null);

  // Close menu/popover on outside click
  useEffect(() => {
    if (!menuOpen && !stageOpen) return undefined;
    const onDoc = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (stageOpen && stageRef.current && !stageRef.current.contains(e.target)) setStageOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen, stageOpen]);

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const handleAnalyze = (e) => {
    stop(e);
    // Deep-link into the standalone V3-native Deal Score engine, with
    // this property pre-selected via the shared useSelectedProperty hook.
    // The property_id also persists to localStorage so all 4 Intel engines
    // (Wholesale / Repair / Comps / Deal Score) stay in the same context.
    nav(`/v3/intel/deal-score?property_id=${p.id}`);
  };

  const handleCopyLink = async (e) => {
    stop(e);
    setMenuOpen(false);
    try {
      const url = `${window.location.origin}/v3/properties/${p.id}`;
      await navigator.clipboard.writeText(url);
      toast.success("Property link copied");
    } catch { toast.error("Couldn't copy link"); }
  };

  const handleDuplicate = async (e) => {
    stop(e);
    setMenuOpen(false);
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/v3/properties/${p.id}/duplicate`);
      onCreate?.(data);
      toast.success("Property duplicated");
      nav(`/v3/properties/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't duplicate property");
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async (e) => {
    stop(e);
    setMenuOpen(false);
    if (busy) return;
    // Optimistic: remove from list right away, restore on failure
    setBusy(true);
    onRemove?.(p.id);
    try {
      await api.post(`/v3/properties/${p.id}/archive`, { reason: "from_card" });
      toast.success("Property archived");
    } catch (err) {
      onCreate?.(p);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't archive property");
    } finally {
      setBusy(false);
    }
  };

  const handleChangeStage = async (newStage) => {
    setStageOpen(false);
    setMenuOpen(false);
    if (newStage === p.stage) return;
    // Optimistic UI
    const previous = p.stage;
    onPatch?.(p.id, { stage: newStage });
    try {
      await api.patch(`/v3/properties/${p.id}`, { stage: newStage });
      toast.success(`Moved to ${STAGE_STYLE[newStage]?.label || newStage}`);
    } catch (err) {
      onPatch?.(p.id, { stage: previous });
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't change stage");
    }
  };

  const handleSavePhoto = async (photoUrl) => {
    const previous = p.hero_photo_url;
    onPatch?.(p.id, { hero_photo_url: photoUrl || null });
    setPhotoOpen(false);
    try {
      await api.patch(`/v3/properties/${p.id}`, {
        hero_photo_url: photoUrl || null,
      });
      toast.success(photoUrl ? "Photo updated" : "Photo removed");
    } catch (err) {
      onPatch?.(p.id, { hero_photo_url: previous ?? null });
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't save photo");
    }
  };

  return (
    <>
      <Link
        to={`/v3/properties/${p.id}`}
        data-testid={`property-card-${p.id}`}
        className="group relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all"
      >
        {/* Hero photo banner — real photo when set, gradient fallback otherwise */}
        <PhotoBanner
          photoUrl={p.hero_photo_url}
          stageColor={stage.color}
          state={p.address?.state}
          onEdit={(e) => { stop(e); setPhotoOpen(true); }}
          testid={`property-card-${p.id}-photo`}
        />

        {/* Hero mini row */}
        <div className="p-5 pb-3 relative overflow-hidden">
          <div
            className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none"
            style={{ backgroundColor: `${stage.color}15` }}
          />
          <div className="relative flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
                {p.address?.state || "—"}
              </div>
              <div className="mt-0.5 text-[16px] font-bold text-slate-900 leading-tight truncate">
                {p.address?.street || "—"}
                {p.address?.unit ? ` #${p.address.unit}` : ""}
              </div>
              <div className="mt-0.5 text-[12px] text-slate-500 flex items-center gap-1 truncate">
                <MapPin className="w-3 h-3 shrink-0" />
                {[p.address?.city, p.address?.state, p.address?.zip_code].filter(Boolean).join(", ")}
              </div>
              {/* Stage chip — click to open popover */}
              <div ref={stageRef} className="relative inline-block">
                <button
                  type="button"
                  data-testid={`property-card-${p.id}-stage-chip`}
                  onClick={(e) => { stop(e); setStageOpen((v) => !v); }}
                  className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ring-1 hover:brightness-95 cursor-pointer transition-all"
                  style={{
                    backgroundColor: `${stage.color}15`,
                    color: stage.color,
                    borderColor: `${stage.color}40`,
                  }}
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: stage.color }} />
                  {stage.label}
                </button>
                {stageOpen && (
                  <div
                    data-testid={`property-card-${p.id}-stage-menu`}
                    onClick={stop}
                    className="absolute left-0 top-full mt-1 w-40 rounded-lg border border-slate-200 bg-white shadow-xl py-1 z-30"
                  >
                    {Object.entries(STAGE_STYLE).map(([key, meta]) => (
                      <button
                        key={key}
                        type="button"
                        data-testid={`property-card-${p.id}-stage-opt-${key}`}
                        onClick={(e) => { stop(e); handleChangeStage(key); }}
                        className={`w-full text-left flex items-center gap-2 px-3 py-1.5 text-[11.5px] font-semibold hover:bg-slate-50 transition-colors ${
                          key === p.stage ? "bg-slate-50" : ""
                        }`}
                      >
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta.color }} />
                        <span className="flex-1 text-slate-700">{meta.label}</span>
                        {key === p.stage && <Check className="w-3 h-3 text-amber-600" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Mini Deal Score ring */}
            <DailyBusinessScore
              score={scoreInfo.value}
              size="sm"
              label={scoreInfo.real ? "Deal score" : "Est score"}
              testid={`property-card-${p.id}-score`}
            />
          </div>
        </div>

        {/* KPI strip */}
        <div className="px-5 py-3 grid grid-cols-3 gap-2 border-t border-slate-100">
          <MiniKpi label="ARV"     value={fmtMoney(kpis.arv.value)}     color="#3B82F6" isReal={kpis.arv.real} />
          <MiniKpi label="MAO"     value={fmtMoney(kpis.mao.value)}     color="#F59E0B" isReal={kpis.mao.real} />
          <MiniKpi label="Margin"  value={fmtMoney(kpis.margin.value)}  color={kpis.margin.value >= 0 ? "#10B981" : "#DC2626"} isReal={kpis.margin.real} negative={kpis.margin.value < 0} />
        </div>

        {/* Sparkline */}
        <div className="px-5 pb-3">
          <div className="rounded-lg bg-slate-50/70 border border-slate-100 px-3 py-2 flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
              7d score
            </div>
            <div className="flex-1">
              <Sparkline data={trend} color={stage.color} height={20} width={140} />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 pb-4 flex items-center gap-1.5 relative">
          <button
            type="button"
            data-testid={`property-card-${p.id}-analyze`}
            onClick={handleAnalyze}
            className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-semibold px-2 py-1.5 transition-colors"
          >
            <Calculator className="w-3 h-3" /> Analyze
          </button>
          <button
            type="button"
            data-testid={`property-card-${p.id}-sms`}
            onClick={(e) => { stop(e); setSmsOpen(true); }}
            className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[11px] font-semibold px-2 py-1.5 transition-colors"
          >
            <MessageSquare className="w-3 h-3" /> SMS
          </button>
          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={(e) => { stop(e); setMenuOpen((v) => !v); }}
              data-testid={`property-card-${p.id}-more`}
              className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 px-2 py-1.5 transition-colors"
            >
              <MoreHorizontal className="w-3 h-3" />
            </button>
            {menuOpen && (
              <div
                data-testid={`property-card-${p.id}-more-menu`}
                onClick={stop}
                className="absolute right-0 bottom-full mb-1 w-48 rounded-lg border border-slate-200 bg-white shadow-xl py-1 z-30"
              >
                <MenuItem
                  icon={Copy} label="Copy link"
                  testid={`property-card-${p.id}-copy-link`}
                  onClick={handleCopyLink}
                />
                <MenuItem
                  icon={GitBranch} label="Change stage"
                  testid={`property-card-${p.id}-menu-stage`}
                  onClick={(e) => { stop(e); setMenuOpen(false); setStageOpen(true); }}
                />
                <MenuItem
                  icon={Files} label="Duplicate"
                  testid={`property-card-${p.id}-duplicate`}
                  onClick={handleDuplicate} disabled={busy}
                />
                <MenuItem
                  icon={ImageIcon} label={p.hero_photo_url ? "Change photo" : "Add photo"}
                  testid={`property-card-${p.id}-photo-menu`}
                  onClick={(e) => { stop(e); setMenuOpen(false); setPhotoOpen(true); }}
                />
                <div className="border-t border-slate-100 my-1" />
                <MenuItem
                  icon={Archive} label="Archive" danger
                  testid={`property-card-${p.id}-archive`}
                  onClick={handleArchive} disabled={busy}
                />
              </div>
            )}
          </div>
        </div>

        {/* Bottom footer — open pill */}
        <div className="border-t border-slate-100 px-5 py-2 flex items-center justify-between bg-slate-50/60">
          <div className="text-[10px] text-slate-500">
            {p.last_activity_at ? `Active ${timeAgo(p.last_activity_at)}` : "New"}
          </div>
          <div className="text-[11px] font-semibold text-amber-600 group-hover:text-amber-700 inline-flex items-center gap-0.5">
            Open <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
      </Link>

      {smsOpen && (
        <SmsModal property={p} onClose={() => setSmsOpen(false)} />
      )}
      {photoOpen && (
        <PhotoDialog
          property={p}
          onSubmit={handleSavePhoto}
          onClose={() => setPhotoOpen(false)}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MenuItem for the ⋯ dropdown
// ─────────────────────────────────────────────────────────────
function MenuItem({ icon: Icon, label, onClick, danger, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full text-left flex items-center gap-2 px-3 py-1.5 text-[12.5px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        danger
          ? "text-rose-600 hover:bg-rose-50"
          : "text-slate-700 hover:bg-slate-50"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// SmsModal — quick-send SMS to seller without leaving the list.
// Uses the real /api/v3/properties/:id/sms endpoint which delegates
// to the user's connected Twilio account. If Twilio is not connected,
// the send is logged to the property timeline and the UI shows a
// friendly "logged, not delivered" state.
// ─────────────────────────────────────────────────────────────
function SmsModal({ property: p, onClose }) {
  const [msg, setMsg] = useState(
    `Hey, checking in on ${p.address?.street || "your property"}. Any updates you'd like to share?`,
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [to, setTo] = useState(p.seller_phone || "");

  const send = async (e) => {
    e.preventDefault();
    if (!to.trim() || !msg.trim()) {
      toast.error("Phone and message are required");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/v3/properties/${p.id}/sms`, {
        to_phone: to.trim(),
        body: msg.trim(),
      });
      setSent(true);
      if (data?.delivered) {
        toast.success("SMS sent");
      } else if (data?.reason === "twilio_not_connected") {
        toast.success("Logged to timeline · Connect Twilio in Settings to deliver");
      } else if (data?.reason === "twilio_error") {
        toast.error(`Logged, but delivery failed: ${data?.detail || "Twilio error"}`);
      } else {
        toast.success("SMS logged");
      }
      setTimeout(onClose, 1100);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send SMS");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
      data-testid="property-sms-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={send}
        className="w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg grid place-items-center text-white shadow-md"
                 style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
              <MessageSquare className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
                Quick SMS
              </div>
              <div className="text-sm font-bold text-slate-900 leading-tight truncate max-w-[280px]">
                {p.address?.street || "Property"}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose}
                  className="p-1 text-slate-400 hover:text-slate-700"
                  aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
              To
            </div>
            <input
              type="tel"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="(555) 555-5555"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              data-testid="property-sms-to"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
              Message · {msg.length}/320
            </div>
            <textarea
              rows={4}
              value={msg}
              onChange={(e) => setMsg(e.target.value.slice(0, 320))}
              maxLength={320}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 resize-none"
              data-testid="property-sms-body"
            />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 bg-slate-50 border-t border-slate-100">
          <button type="button" onClick={onClose}
                  className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5">
            Cancel
          </button>
          <button
            type="submit"
            disabled={sending || sent}
            data-testid="property-sms-send"
            className="inline-flex items-center gap-1.5 rounded-lg text-white text-sm font-semibold px-4 py-1.5 shadow-md disabled:opacity-60"
            style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
          >
            {sent ? (
              <><Check className="w-3.5 h-3.5" /> Sent</>
            ) : sending ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
            ) : (
              <><Send className="w-3.5 h-3.5" /> Send SMS</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PhotoDialog — quick-set the property's hero photo via a URL.
// This is intentionally URL-first (paste a Zillow/MLS/Google image link)
// so we can ship real photos without a full upload pipeline. A future
// iteration can add drag-drop + object-storage upload.
// ─────────────────────────────────────────────────────────────
function PhotoDialog({ property: p, onSubmit, onClose }) {
  const [url, setUrl] = useState(p.hero_photo_url || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const clean = url.trim();
    if (clean && !/^https?:\/\//i.test(clean)) {
      toast.error("Photo URL must start with http:// or https://");
      return;
    }
    setBusy(true);
    await onSubmit(clean);
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    await onSubmit("");
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center"
         data-testid={`property-card-${p.id}-photo-dialog`}>
      <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit}
            className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden animate-in fade-in-90 zoom-in-95">
        <div className="h-1 w-full" style={{ backgroundColor: "#F59E0B" }} />
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Camera className="w-5 h-5 text-amber-600" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
              Hero photo
            </div>
            <div className="text-[15px] font-bold text-slate-900 leading-tight truncate">
              {p.address?.street || "Property"}
            </div>
          </div>
          <button type="button" onClick={onClose}
                  className="text-slate-400 hover:text-slate-600 text-lg leading-none">×</button>
        </div>

        {url && /^https?:\/\//i.test(url.trim()) && (
          <div className="border-b border-slate-100 bg-slate-50/60 p-3">
            <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
              Preview
            </div>
            <div className="relative w-full h-40 rounded-lg overflow-hidden bg-slate-100">
              <img src={url.trim()}
                   alt="Property preview"
                   onError={(e) => { e.currentTarget.style.opacity = "0"; }}
                   className="w-full h-full object-cover transition-opacity"
                   draggable={false} />
            </div>
          </div>
        )}

        <div className="p-5 space-y-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
              Photo URL
            </div>
            <input
              type="url" value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…/photo.jpg"
              autoFocus
              data-testid={`property-card-${p.id}-photo-url-input`}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
            />
            <div className="mt-1 text-[10.5px] text-slate-500">
              Paste a direct link from Zillow, MLS, Google Street View, or any hosted image.
            </div>
          </label>
        </div>

        <div className="border-t border-slate-100 px-5 py-3 flex justify-between items-center gap-2">
          {p.hero_photo_url ? (
            <button type="button" onClick={remove}
                    disabled={busy}
                    data-testid={`property-card-${p.id}-photo-remove`}
                    className="text-[12px] font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-60">
              Remove photo
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
                    className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[13px] font-semibold px-3 py-2">
              Cancel
            </button>
            <button type="submit"
                    disabled={busy}
                    data-testid={`property-card-${p.id}-photo-save`}
                    className="rounded-lg text-white text-[13px] font-semibold px-4 py-2 shadow-md disabled:opacity-60"
                    style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
              {busy ? <Loader2 className="w-4 h-4 inline animate-spin" /> : <Check className="w-4 h-4 inline mr-1" />}
              Save
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function MiniKpi({ label, value, color, isReal, negative }) {
  return (
    <div title={isReal ? "Live from published engine" : "Estimated — publish an engine to lock this in"}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-slate-500">
        <span className="h-1 w-1 rounded-full" style={{ backgroundColor: color }} />
        {label}
        {isReal && (
          <span
            className="h-1 w-1 rounded-full bg-emerald-500"
            title="Real value from published engine"
          />
        )}
      </div>
      <div
        className={`mt-0.5 text-[14px] font-bold tabular-nums ${
          isReal
            ? (negative ? "text-rose-600" : "text-slate-900")
            : "text-slate-400 italic"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PhotoBanner — real hero photo when set, otherwise a rich
// stage-tinted gradient with the state code centered.
// ─────────────────────────────────────────────────────────────
function PhotoBanner({ photoUrl, stageColor, state, onEdit, testid }) {
  const [broken, setBroken] = useState(false);
  const hasPhoto = !!photoUrl && !broken;
  return (
    <div className="relative w-full h-24 overflow-hidden" data-testid={testid}>
      {hasPhoto ? (
        <img
          src={photoUrl}
          alt=""
          onError={() => setBroken(true)}
          className="w-full h-full object-cover"
          draggable={false}
          loading="lazy"
        />
      ) : (
        <div
          className="w-full h-full grid place-items-center relative"
          style={{
            backgroundImage: `linear-gradient(135deg, ${stageColor}30 0%, ${stageColor}90 100%)`,
          }}
        >
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.5) 0%, transparent 40%)," +
                "radial-gradient(circle at 80% 70%, rgba(0,0,0,0.15) 0%, transparent 40%)",
            }}
          />
          <div className="relative text-white font-black text-3xl tracking-widest drop-shadow-sm">
            {(state || "—").toUpperCase()}
          </div>
        </div>
      )}
      {/* Stage-color underscore beneath the photo, keeps the tag visible */}
      <div className="absolute bottom-0 left-0 right-0 h-1" style={{ backgroundColor: stageColor }} />
      {/* Change-photo affordance on hover */}
      <button
        type="button"
        onClick={onEdit}
        data-testid={`${testid}-edit`}
        title={hasPhoto ? "Change photo" : "Add photo"}
        className="absolute top-2 right-2 h-7 w-7 rounded-full grid place-items-center bg-black/40 hover:bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm"
      >
        <Camera className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function timeAgo(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const days = Math.floor(ms / (86400 * 1000));
    if (days === 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.round(days / 7)}w ago`;
    return `${Math.round(days / 30)}mo ago`;
  } catch { return "recently"; }
}

// ─────────────────────────────────────────────────────────────
// PropertyTable — fallback list view
// ─────────────────────────────────────────────────────────────
function PropertyTable({ items }) {
  const nav = useNavigate();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm"
         data-testid="properties-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          <tr>
            <th className="text-left px-4 py-2.5">Address</th>
            <th className="text-left px-4 py-2.5">Stage</th>
            <th className="text-left px-4 py-2.5">Score</th>
            <th className="text-left px-4 py-2.5">Last Activity</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => {
            const stage = STAGE_STYLE[p.stage] || STAGE_STYLE.lead;
            return (
              <tr key={p.id}
                  className="border-t border-slate-100 hover:bg-amber-50/40 cursor-pointer"
                  onClick={() => nav(`/v3/properties/${p.id}`)}
                  data-testid={`property-row-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="text-slate-900 font-semibold">
                    {p.address?.street || "—"}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {[p.address?.city, p.address?.state, p.address?.zip_code].filter(Boolean).join(", ")}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ring-1"
                        style={{
                          backgroundColor: `${stage.color}15`,
                          color: stage.color,
                          borderColor: `${stage.color}40`,
                        }}>
                    {stage.label}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-900 tabular-nums font-semibold">
                  {resolveScore(p).value}
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">
                  {p.last_activity_at ? new Date(p.last_activity_at).toLocaleString() : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CreateModal — address + deal type (House/Land/Multifamily/New Construction)
// + optional asking price / ARV so the deal is analyzable on save.
// Remembers last-used deal_type via localStorage.
// ─────────────────────────────────────────────────────────────
const DEAL_TYPES = [
  { key: "house",            label: "House",            icon: HomeIcon,   hint: "SF / condo / townhome" },
  { key: "land",             label: "Land",             icon: TreesIcon,  hint: "Raw land / lot" },
  { key: "multifamily",      label: "Multifamily",      icon: Building2,  hint: "Duplex / 2-4 unit / apt" },
  { key: "new_construction", label: "New Construction", icon: HammerIcon, hint: "Build-to-sell / spec" },
];

function CreateModal({ creating, onClose, onSubmit }) {
  const lastDealType = (() => {
    try { return localStorage.getItem("v3_last_deal_type") || "house"; }
    catch { return "house"; }
  })();
  const [form, setForm] = useState({
    street: "", city: "", state: "TX", zip_code: "",
    apn: "", lead_source: "", seller_phone: "",
    tcpa_attest: false,
    deal_type: lastDealType,
    asking_price: "",
    estimated_arv: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const hasPhone = form.seller_phone.trim().length > 0;
  const canSubmit = !hasPhone || form.tcpa_attest;
  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    // Persist last-used deal type so next "Add Property" defaults to it.
    try { localStorage.setItem("v3_last_deal_type", form.deal_type); } catch { /* noop */ }
    const parseMoney = (v) => {
      const cleaned = String(v).replace(/[$,\s]/g, "").trim();
      if (!cleaned) return undefined;
      const n = Number(cleaned);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    };
    onSubmit({
      address: {
        street: form.street.trim(),
        city: form.city.trim(),
        state: form.state.trim().toUpperCase(),
        zip_code: form.zip_code.trim(),
      },
      apn: form.apn.trim() || undefined,
      lead_source: form.lead_source.trim() || undefined,
      seller_phone: form.seller_phone.trim() || undefined,
      tcpa_consent_attested: form.tcpa_attest || undefined,
      tcpa_consent_attested_at: form.tcpa_attest ? new Date().toISOString() : undefined,
      deal_type: form.deal_type || "house",
      asking_price: parseMoney(form.asking_price),
      estimated_arv: parseMoney(form.estimated_arv),
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center px-4"
         onClick={onClose} data-testid="create-property-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="w-full max-w-lg rounded-2xl bg-white border border-slate-200 p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-9 w-9 rounded-lg grid place-items-center text-white shadow-md"
               style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
            <MapPin className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">New Deal</div>
            <h2 className="text-lg font-bold text-slate-900 leading-tight">Add a property</h2>
          </div>
        </div>

        {/* Deal type selector — 2×2 grid of tappable cards */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
            Deal Type *
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="create-deal-type-grid">
            {DEAL_TYPES.map(({ key, label, icon: Icon, hint }) => {
              const active = form.deal_type === key;
              return (
                <button
                  key={key} type="button"
                  onClick={() => setForm((f) => ({ ...f, deal_type: key }))}
                  data-testid={`create-deal-type-${key}`}
                  className={
                    "flex items-start gap-2 rounded-lg border px-3 py-2.5 text-left transition-all " +
                    (active
                      ? "border-amber-500 bg-amber-50 shadow-sm ring-1 ring-amber-400"
                      : "border-slate-200 bg-white hover:border-slate-300")
                  }
                >
                  <Icon className={"w-4 h-4 mt-0.5 shrink-0 " + (active ? "text-amber-600" : "text-slate-500")} />
                  <div className="min-w-0">
                    <div className={"text-[13px] font-semibold leading-tight " + (active ? "text-amber-900" : "text-slate-900")}>
                      {label}
                    </div>
                    <div className="text-[10.5px] text-slate-500 leading-tight mt-0.5">
                      {hint}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Field label="Street" required>
            <input required value={form.street} onChange={set("street")} data-testid="create-street"
                   className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400" />
          </Field>
          <div className="grid grid-cols-[2fr_1fr_1fr] gap-2">
            <Field label="City" required>
              <input required value={form.city} onChange={set("city")} data-testid="create-city"
                     className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400" />
            </Field>
            <Field label="State" required>
              <input required maxLength={2} value={form.state} onChange={set("state")} data-testid="create-state"
                     className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400" />
            </Field>
            <Field label="ZIP" required>
              <input required value={form.zip_code} onChange={set("zip_code")} data-testid="create-zip"
                     className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="APN (optional)">
              <input value={form.apn} onChange={set("apn")} data-testid="create-apn"
                     className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400" />
            </Field>
            <Field label="Lead Source">
              <input value={form.lead_source} onChange={set("lead_source")} data-testid="create-lead-source"
                     className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400" />
            </Field>
          </div>

          {/* Money fields — optional but pre-populate the Strategy Card so the
              deal is analyzable the moment it lands in Pipeline. */}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Asking Price (optional)">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <input
                  inputMode="decimal" value={form.asking_price} onChange={set("asking_price")}
                  data-testid="create-asking-price"
                  placeholder="0"
                  className="w-full bg-white border border-slate-200 rounded-lg pl-6 pr-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                />
              </div>
            </Field>
            <Field label={form.deal_type === "land" ? "Finished Lot Value (ARV)" : "Estimated ARV (optional)"}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <input
                  inputMode="decimal" value={form.estimated_arv} onChange={set("estimated_arv")}
                  data-testid="create-estimated-arv"
                  placeholder="0"
                  className="w-full bg-white border border-slate-200 rounded-lg pl-6 pr-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                />
              </div>
            </Field>
          </div>

          <Field label="Seller Phone (optional)">
            <input
              type="tel" value={form.seller_phone} onChange={set("seller_phone")}
              data-testid="create-seller-phone"
              placeholder="+1 555 123 4567"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
            />
          </Field>

          {/* TCPA attestation — required whenever a phone is entered so
              Jarvis is legally cleared to text this seller. */}
          {hasPhone && (
            <label
              className="flex items-start gap-3 mt-1 p-3 rounded-lg border cursor-pointer transition-colors border-amber-200 bg-amber-50/40"
              data-testid="create-tcpa-attest-row"
            >
              <input
                type="checkbox"
                checked={form.tcpa_attest}
                onChange={(e) => setForm((f) => ({ ...f, tcpa_attest: e.target.checked }))}
                className="mt-0.5 w-4 h-4 shrink-0"
                data-testid="create-tcpa-attest-checkbox"
                required
              />
              <span className="text-[11.5px] leading-snug text-slate-700">
                I confirm this seller gave <strong>express written consent</strong> to be
                contacted at this number, including automated / AI-drafted SMS from
                Jarvis. I have the signed opt-in, verbal recording, or web-form
                submission on file and can produce it on request.
              </span>
            </label>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} data-testid="create-cancel"
                  className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">
            Cancel
          </button>
          <button type="submit" disabled={creating || !canSubmit} data-testid="create-submit"
                  className="rounded-lg text-white text-sm font-semibold px-4 py-2 shadow-md disabled:opacity-60"
                  style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
            {creating ? "Creating…" : "Create Property"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
        {label}{required ? " *" : ""}
      </div>
      {children}
    </label>
  );
}
